import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import "./TransactionDetails.css";
import { decrypt } from "../../Encrypdecryp/Crypt";
import * as pdfjsLib from "pdfjs-dist";
import JsonEditor from '../../components/JsonEditor';
import AuditTrail from './AuditTrail';
import AlertsAndNotifications from './AlertsAndNotifications';
import RerunValidationsButton from './RerunValidationsButton';

// Configure PDF worker
const workerSrc = "https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js";
const fallbackWorkerSrc = "https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js";
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// Use environment variable for API URL and validate
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error("API_BASE_URL is not defined in .env");
}

// Document icon dictionary
const iconDictionary = {
  "Proforma Invoice": "✨",
  "Purchase Order": "📦",
  "Shipment": "🚚",
  "Invoice": "💳",
  "Commercial Invoice": "💳",
  "Packing List": "📋",
  "Bill of Lading": "🚢",
  "Airway Bill": "✈️",
  "Goods Declaration": "📑",
  "Import General Manifest": "📊",
  "L/C Application": "🏦",
  "L/C Issuance": "📜",
  "Certificate of Origin": "🌍",
  "Inspection Certificate": "🔍",
  "Bill of Exchange": "💸",
  "L/C Settlement Document": "✅",
  "Delivery Order": "📥",
  "Bank remittance receipt": "💵",
  "FE Contract": "💱",
  "Bank Payment Approval": "✔️",
  "Consignment Agreement": "🤝",
  "Sales Report": "📈",
  "Advance Payment": "💸",
  "Letter of Credit": "🏦",
  "Open Account": "📖",
  "Documentary Collection (D/P)": "📜",
  "Documentary Collection (D/A)": "📜",
  "Import contract form": "📜",
  "Bank Contract": "🤝",
  "Consignment Basis": "📦",
  "default": "📌",
};

// Load document flow from localStorage or use defaults
const loadDocumentFlow = () => {
  try {
    const stored = localStorage.getItem("paymentMethodDocuments");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error loading payment method documents:", error);
  }
  // Return default flow if nothing is stored
  return {
    "Advance Payment": [
      "Proforma Invoice",
      "Bank remittance receipt",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Goods Declaration",
      "Import General Manifest",
    ],
    "Letter of Credit": [
      "Proforma Invoice",
      "L/C Application",
      "L/C Issuance",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Certificate of Origin",
      "Inspection Certificate",
      "Bill of Lading",
      "Bill of Exchange",
      "Goods Declaration",
      "L/C Settlement Document",
    ],
    "Open Account": [
      "Proforma Invoice",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Delivery Order",
      "Goods Declaration",
    ],
    "Documentary Collection": [
      "Proforma Invoice",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Goods Declaration",
    ],
    "Bank Contract": [
      "Proforma Invoice",
      "FE Contract",
      "Purchase Order",
      "Shipment",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Goods Declaration",
      "Bank Payment Approval",
      "Import contract form",
    ],
    "Consignment Basis": [
      "Proforma Invoice",
      "Consignment Agreement",
      "Packing List",
      "Commercial Invoice",
      "Bill of Lading",
      "Goods Declaration",
      "Sales Report",
    ],
  };
};

// Mapping for document names
const documentNameMapping = {
  "Invoice": "Commercial Invoice",
};

// Document keywords for fuzzy matching
const documentKeywords = {
  "Proforma Invoice": {
    required: ["proforma", "pi"],
    optional: ["invoice", "pro forma", "performa"],
    exclusions: ["commercial", "final"],
  },
  "Purchase Order": {
    required: ["purchase", "po"],
    optional: ["order", "buy", "procurement"],
    exclusions: ["delivery"],
  },
  Shipment: {
    required: ["shipment", "ship"],
    optional: ["shipping", "dispatch"],
    exclusions: ["bill", "invoice"],
  },
  "Commercial Invoice": {
    required: ["commercial"],
    optional: ["invoice", "ci", "bill"],
    exclusions: ["proforma", "pro forma"],
  },
  Invoice: {
    required: ["invoice"],
    optional: ["bill"],
    exclusions: ["proforma", "commercial", "pro forma"],
  },
  Payment: {
    required: ["payment"],
    optional: ["pay", "remittance"],
    exclusions: ["advance", "bank", "approval"],
  },
  "Packing List": {
    required: ["packing", "pl"],
    optional: ["pack", "list"],
    exclusions: ["invoice", "bill"],
  },
  "Bill of Lading": {
    required: ["lading", "bl", "bol"],
    optional: ["bill"],
    exclusions: ["airway", "exchange"],
  },
  "Airway Bill": {
    required: ["airway", "awb"],
    optional: ["air", "bill"],
    exclusions: ["lading", "exchange"],
  },
  "Goods Declaration": {
    required: ["goods", "gd"],
    optional: ["declaration", "declare"],
    exclusions: ["import", "manifest"],
  },
  "Import General Manifest": {
    required: ["import", "manifest", "igm"],
    optional: ["general"],
    exclusions: ["contract", "form"],
  },
  "L/C Application": {
    required: ["lc", "application"],
    optional: ["letter", "credit", "apply"],
    exclusions: ["issuance", "settlement"],
  },
  "L/C Issuance": {
    required: ["lc", "issuance"],
    optional: ["letter", "credit", "issue"],
    exclusions: ["application", "settlement"],
  },
  "Certificate of Origin": {
    required: ["origin", "coo"],
    optional: ["certificate", "cert"],
    exclusions: ["inspection"],
  },
  "Inspection Certificate": {
    required: ["inspection"],
    optional: ["certificate", "inspect", "cert"],
    exclusions: ["origin"],
  },
  "Bill of Exchange": {
    required: ["exchange", "boe"],
    optional: ["bill"],
    exclusions: ["lading", "airway"],
  },
  "L/C Settlement Document": {
    required: ["lc", "settlement"],
    optional: ["letter", "credit", "settle", "document"],
    exclusions: ["application", "issuance"],
  },
  "Delivery Order": {
    required: ["delivery", "do"],
    optional: ["order", "deliver"],
    exclusions: ["purchase"],
  },
  "Bank remittance receipt": {
    required: ["bank", "remittance"],
    optional: ["receipt", "remit"],
    exclusions: ["contract", "payment", "approval"],
  },
  "FE Contract": {
    required: ["fe", "foreign", "exchange"],
    optional: ["contract"],
    exclusions: ["import", "bank"],
  },
  "Bank Payment Approval": {
    required: ["bank", "payment", "approval"],
    optional: ["approve"],
    exclusions: ["remittance", "contract", "fe"],
  },
  "Consignment Agreement": {
    required: ["consignment", "agreement"],
    optional: ["consign"],
    exclusions: ["basis"],
  },
  "Sales Report": {
    required: ["sales", "report"],
    optional: ["sale"],
    exclusions: ["consignment"],
  },
  "Advance Payment": {
    required: ["advance"],
    optional: ["payment", "prepayment", "pre"],
    exclusions: ["bank", "approval"],
  },
  "Letter of Credit": {
    required: ["letter", "credit"],
    optional: ["lc"],
    exclusions: ["application", "issuance", "settlement"],
  },
  "Open Account": {
    required: ["open", "account"],
    optional: [],
    exclusions: ["letter", "credit"],
  },
  "Documentary Collection (D/P)": {
    required: ["documentary", "dp", "d/p"],
    optional: ["collection"],
    exclusions: ["d/a", "da"],
  },
  "Documentary Collection (D/A)": {
    required: ["documentary", "da", "d/a"],
    optional: ["collection"],
    exclusions: ["d/p", "dp"],
  },
  "Import contract form": {
    required: ["import", "contract"],
    optional: ["form", "request", "application"],
    exclusions: ["fe", "foreign", "exchange", "bank"],
  },
  "Bank Contract": {
    required: ["bank", "contract"],
    optional: [],
    exclusions: ["import", "fe", "foreign", "exchange"],
  },
  "Consignment Basis": {
    required: ["consignment", "basis"],
    optional: [],
    exclusions: ["agreement"],
  },
};

// Function to calculate similarity score between two strings
const calculateSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  // Exact match
  if (s1 === s2) return 1.0;
  
  // Check if one string contains the other
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;
  
  // Calculate word-based similarity
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);
  
  let matchedWords = 0;
  const totalWords = Math.max(words1.length, words2.length);
  
  for (const word1 of words1) {
    for (const word2 of words2) {
      if (word1 === word2 || word1.includes(word2) || word2.includes(word1)) {
        matchedWords++;
        break;
      }
    }
  }
  
  return matchedWords / totalWords;
};

// Enhanced fuzzy matching function
const fuzzyMatchDocument = (uploadedDocName, requiredDocName) => {
  const uploaded = uploadedDocName.toLowerCase().trim();
  const required = requiredDocName.toLowerCase().trim();
  
  // Direct similarity check
  const directSimilarity = calculateSimilarity(uploaded, required);
  if (directSimilarity >= 0.8) return true;
  
  // Get document-specific keywords
  const docKeywords = documentKeywords[requiredDocName];
  if (!docKeywords) return false;
  
  // Check for exclusions first
  if (docKeywords.exclusions) {
    for (const exclusion of docKeywords.exclusions) {
      if (uploaded.includes(exclusion.toLowerCase())) {
        return false;
      }
    }
  }
  
  // Check if ALL required keywords are present
  let requiredMatches = 0;
  const requiredKeywords = docKeywords.required || [];
  
  for (const reqKeyword of requiredKeywords) {
    if (uploaded.includes(reqKeyword.toLowerCase())) {
      requiredMatches++;
    }
  }
  
  if (requiredMatches === requiredKeywords.length) {
    return true;
  }
  
  // If at least one required keyword matches, check optional keywords
  if (requiredMatches > 0) {
    let optionalMatches = 0;
    const optionalKeywords = docKeywords.optional || [];
    
    for (const optKeyword of optionalKeywords) {
      if (uploaded.includes(optKeyword.toLowerCase())) {
        optionalMatches++;
      }
    }
    
    if (optionalMatches > 0) {
      return true;
    }
  }
  
  return false;
};

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box className="td-error">
          Something went wrong: {this.state.error?.message || "Unknown error"}
        </Box>
      );
    }
    return this.props.children;
  }
}

const TransactionDetails = ({ collapsed = false, alerts = [], onAcknowledgeAlert }) => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [chainData, setChainData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [workerError, setWorkerError] = useState(null);
  const [dataError, setDataError] = useState(null);
  const [validations, setValidations] = useState([]);
  const [isRerunningValidations, setIsRerunningValidations] = useState(false);
  const [isFetchingValidations, setIsFetchingValidations] = useState(false);
  const [showValidationResults, setShowValidationResults] = useState(false);
  const [userRole, setUserRole] = useState('normal');
  const [currentDocumentFlow, setCurrentDocumentFlow] = useState(loadDocumentFlow());

  const auditTrail = [
    { id: 1, event: "Transaction Created", date: "2025-03-24 10:00", user: "admin" },
    { id: 2, event: "Invoice Uploaded", date: "2025-03-25 14:00", user: "Admin" },
    { id: 3, event: "Validation Failed", date: "2025-04-01 09:00", user: "system" },
  ];

  // Add useEffect to reload document flow when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      const updatedFlow = loadDocumentFlow();
      setCurrentDocumentFlow(updatedFlow);
      console.log("Document flow reloaded in TransactionDetails:", updatedFlow);
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Format date consistently with TransactionChainPage
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString('en-US', {
        timeZone: 'Asia/Karachi',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).replace(/,/, '');
    } catch {
      return 'Invalid Date';
    }
  };

  // Format trading partners with debug logging
  const formatTradingPartners = (partners) => {
    console.log('formatTradingPartners input:', partners);
    if (!partners) {
      console.log('formatTradingPartners returning "Unknown": partners is null or undefined');
      return 'Unknown';
    }
    if (typeof partners === 'string') {
      const trimmed = partners.trim();
      console.log('formatTradingPartners string case:', { original: partners, trimmed });
      return trimmed || 'Unknown';
    }
    if (typeof partners === 'object' && partners !== null) {
      const { seller, buyer } = partners;
      console.log('formatTradingPartners object case:', { seller, buyer });
      return seller && buyer ? `${seller} vs. ${buyer}` : seller || buyer || 'Unknown';
    }
    console.log('formatTradingPartners returning "Unknown": default case');
    return 'Unknown';
  };

  // Filter alerts for this transaction
  const transactionAlerts = alerts.filter(
    alert => alert.transaction_id?.toString() === transactionId
  );

  const getRequiredDocumentIcons = () => {
    if (!chainData?.payment_type) return <span className="text-secondary">No payment type specified.</span>;
    
    // Get required documents from current document flow (from localStorage/setup)
    const requiredDocs = currentDocumentFlow[chainData.payment_type] || [];
    const uploadedDocs = chainData.chain?.map(stage => stage.name) || [];
    
    console.log('TransactionDetails - Required Documents:', {
      paymentType: chainData.payment_type,
      requiredDocs,
      uploadedDocs,
      currentDocumentFlow
    });

    return (
      <div className="doc-scroller">
        {requiredDocs.map((doc, idx) => {
          const isPresent = uploadedDocs.some((uploaded) => {
            const mappedUploaded = documentNameMapping[uploaded] || uploaded;
            
            // Exact match check
            if (mappedUploaded.toLowerCase() === doc.toLowerCase()) {
              return true;
            }
            
            // Fuzzy match check
            return fuzzyMatchDocument(mappedUploaded, doc);
          });
          
          const icon = iconDictionary[doc] || iconDictionary.default;
          const status = isPresent ? "Added" : "Missing";
          
          return (
            <div key={idx}>
              <div className={`required-doc ${!isPresent ? "missing" : ""}`}>
                <span className="doc-icon">{icon}</span>
                <span className="doc-name">{doc}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('No authentication token found.');
        const response = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const isSuperuser = response.data.is_superuser === 'True';
        setUserRole(isSuperuser ? 'admin' : 'normal');
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('normal');
        toast.error(
          `Failed to fetch user role: ${err.response?.data?.message || err.message}`,
          {
            position: "bottom-right",
            autoClose: 3000,
            className: 'toast-error',
          }
        );
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    const testWorker = async () => {
      try {
        let response = await fetch(workerSrc);
        if (!response.ok) {
          console.warn(`Primary worker failed: ${response.statusText}. Trying fallback.`);
          response = await fetch(fallbackWorkerSrc);
          if (!response.ok) {
            throw new Error(`Fallback worker failed: ${response.statusText}`);
          }
        }
        setWorkerError(null);
      } catch (err) {
        console.error("Worker load error:", err);
        setWorkerError("Failed to load PDF worker. PDF previews may be unavailable.");
      }
    };
    testWorker();
  }, []);

  useEffect(() => {
    const fetchTransaction = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found. Please log in again.");

        const transactionResponse = await axios.get(
          `${API_BASE_URL}/transaction?transaction_id=${transactionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const apiTransaction = transactionResponse.data;

        if (!apiTransaction) throw new Error("Transaction not found.");

        const chainResponse = await axios.get(
          `${API_BASE_URL}/transactions_with_chain`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const chainDataArray = Array.isArray(chainResponse.data)
          ? chainResponse.data
          : chainResponse.data.data || [];
        console.log("Raw chain data response:", chainDataArray);

        const transactionChain = chainDataArray.find((chain) => chain.id.toString() === transactionId);

        if (!transactionChain) {
          console.error(`No chain data found for transactionId: ${transactionId}`);
          throw new Error("Transaction chain not found for this ID.");
        }

        console.log("Selected chain data:", transactionChain);
        console.log("Has validations:", transactionChain.has_validations);
        setChainData(transactionChain);

        const files = await Promise.all(
          (apiTransaction.documents || []).map(async (doc, index) => {
            let pdfUrl = null;
            let extractedData = null;

            try {
              const fileName = doc.pdf_path.split("/storage/")[1];
              const pdfResponse = await axios.get(
                `${API_BASE_URL}/storage/${fileName}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                }
              );
              pdfUrl = URL.createObjectURL(pdfResponse.data);
            } catch (err) {
              console.warn(`Failed to fetch PDF for document ${doc.id}:`, err);
            }

            try {
              const dataResponse = await axios.get(
                `${API_BASE_URL}/transactions/${transactionId}/documents/${doc.id}/mongo-data`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              console.log(`Extracted data for document ${doc.id}:`, dataResponse.data);
              extractedData = dataResponse.data?.mongo_data?.finalized_data || null;
              if (!extractedData) {
                console.warn(`No finalized_data found for document ${doc.id}`);
                setDataError(`No finalized data available for document ${doc.id}.`);
              }
            } catch (err) {
              console.warn(`Failed to fetch extracted data for document ${doc.id}:`, err);
              let errorMessage = `Failed to load extracted data for document ${doc.id}: ${err.response?.data?.message || err.message}`;
              if (err.response?.status === 404) {
                errorMessage = `Data not found for document ${doc.id}.`;
              } else if (err.response?.status === 403) {
                errorMessage = `Access denied for document ${doc.id}. Please log in again or contact support.`;
              }
              setDataError(errorMessage);
            }

            const chainStage = transactionChain.chain[index] || {};
            let docName;
            try {
              docName = chainStage.name || decrypt(doc.type) || "Unknown";
            } catch (decryptErr) {
              console.warn(`Decryption error for document ${doc.id} type:`, decryptErr);
              docName = "Unknown";
            }

            return {
              name: docName,
              docType: docName,
              pdfUrl,
              extractedData,
              id: doc.id.toString(),
            };
          })
        );

        console.log("Processed files:", files);

        const transformedTransaction = {
          id: apiTransaction.id.toString(),
          name: decrypt(apiTransaction.name) || `Transaction ${apiTransaction.id}`,
          status: apiTransaction.status || "Under Review",
          createdAt: apiTransaction.created_at || apiTransaction.last_updated || "2025-03-24",
          updatedAt: apiTransaction.last_updated || "2025-04-25",
          totalAmount: files.reduce(
            (sum, file) => sum + (file.extractedData?.documentDetails?.totalAmount || 0),
            0
          ),
          parties: {
            supplier: files[0]?.extractedData?.tradingPartner?.seller?.name || "Unknown",
            buyer: files[0]?.extractedData?.tradingPartner?.buyer?.name || "Unknown",
          },
          files,
          refNo: apiTransaction.refNo || `REF${apiTransaction.id}`,
          transaction_name: decrypt(apiTransaction.name) || `Transaction ${apiTransaction.id}`,
          lastUpdateDate: apiTransaction.last_updated || "2025-04-25",
          chain: transactionChain.chain.map((stage) => ({
            name: stage.name,
            extractedData: files.find((file) => file.docType === stage.name)?.extractedData || null,
          })),
        };

        setTransaction(transformedTransaction);
        if (transformedTransaction.files.length > 0) {
          setSelectedFileIndex(0);
          setPdfUrl(transformedTransaction.files[0].pdfUrl);
          setExtractedData(transformedTransaction.files[0].extractedData);
          console.log("Set initial states:", {
            selectedFileIndex: 0,
            pdfUrl: transformedTransaction.files[0].pdfUrl,
            extractedData: transformedTransaction.files[0].extractedData,
          });
        } else {
          console.warn("No files available for transaction");
        }

        if (transactionChain.has_validations) {
          setShowValidationResults(true);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching transaction:", err);
        let errorMessage = "Failed to load transaction: " + (err.response?.data?.message || err.message || "Unknown error");
        if (err.response?.status === 403) {
          errorMessage = "Access denied: Invalid or expired token. Please log in again.";
          localStorage.removeItem("authToken");
          navigate("/login");
        }
        setError(errorMessage);
        setLoading(false);
        toast.error(errorMessage, {
          position: "bottom-right",
          autoClose: 3000,
          className: 'toast-error',
        });
      }
    };

    fetchTransaction();
  }, [transactionId, navigate]);

  const handleRerunValidations = async () => {
    console.log("🔄 handleRerunValidations called");
    setIsRerunningValidations(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found. Please log in again.");

      const response = await axios.post(
        `${API_BASE_URL}/documents/validate-all`,
        { transaction_set_id: parseInt(transactionId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("✅ Validation response received:", response.data);

      const fetchedValidations = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
      setValidations(fetchedValidations);
      setShowValidationResults(true);

      console.log("🎉 About to show success toast");
      toast.success("All validations have been rerun successfully.", {
        position: "bottom-right",
        autoClose: 1500,
        className: 'toast-success',
      });
      console.log("✅ Toast.success called");

      if (window.onValidationsComplete) {
        console.log("📢 Calling window.onValidationsComplete");
        window.onValidationsComplete(transactionId, fetchedValidations);
      } else {
        console.warn("⚠️ window.onValidationsComplete is not defined");
      }
    } catch (err) {
      console.error("Error rerunning validations:", err);
      let errorMessage = "Failed to rerun validations: " + (err.response?.data?.message || err.message || "Unknown error");
      if (err.response?.status === 403) {
        errorMessage = "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      }
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        className: 'toast-error',
      });
    } finally {
      setIsRerunningValidations(false);
    }
  };

  const handleRuntimeValidation = async () => {
    setIsFetchingValidations(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found. Please log in again.");

      const response = await axios.get(
        `${API_BASE_URL}/documents/validations/${transactionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fetchedValidations = (Array.isArray(response.data) ? response.data : response.data.data || []).map((v) => ({
        id: v.id,
        transactionId: v.transaction_set_id,
        transactionName: transaction?.transaction_name || `Transaction ${v.transaction_set_id}`,
        checkName: v.validation_check,
        status: v.status === "FAILED" ? "Fail" : v.status,
        lastRun: v.last_run,
        evidence: Array.isArray(v.reason_evidence)
          ? v.reason_evidence.join("; ")
          : v.reason_evidence?.discrepancies?.join("; ") || "",
        details: Array.isArray(v.reason_evidence)
          ? v.reason_evidence[0] || ""
          : v.reason_evidence?.discrepancies?.[0] || "",
        documentIds: v.document_ids,
        progress: 100,
        changeHistory: [],
        highlightFields: [],
        module: 3,
      }));

      setValidations(fetchedValidations);
      setShowValidationResults(true);

      navigate(`/runtime-validation?transactionId=${transactionId}&module=0`, {
        state: {
          validations: fetchedValidations,
          transactionId,
          transactionName: transaction?.transaction_name || `Transaction ${transactionId}`,
        },
      });

      toast.success("Validation results have been loaded.", {
        position: "bottom-right",
        autoClose: 1500,
        className: 'toast-success',
      });
    } catch (err) {
      console.error("Error fetching validations:", err);
      let errorMessage = "Failed to fetch validations: " + (err.response?.data?.message || err.message || "Unknown error");
      if (err.response?.status === 403) {
        errorMessage = "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      }
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        className: 'toast-error',
      });
    } finally {
      setIsFetchingValidations(false);
    }
  };

  const handleFileSelect = (index) => {
    setSelectedFileIndex(index);
    setPdfUrl(transaction.files[index].pdfUrl);
    setExtractedData(transaction.files[index].extractedData);
    console.log("Selected file extractedData:", transaction.files[index].extractedData);
    setDataError(transaction.files[index].extractedData ? null : `No finalized data available for document ${transaction.files[index].id}.`);
  };

  const handleViewValidations = (moduleId) => {
    navigate(`/runtime-validation?transactionId=${transactionId}&module=${moduleId}`, {
      state: {
        validations,
        transactionId,
        transactionName: transaction.transaction_name,
      },
    });
  };

  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const filteredFiles = transaction?.files || [];

  console.log("Render states:", { transaction, filteredFiles, pdfUrl, extractedData, loading, error, chainData, hasValidations: chainData?.has_validations });

  if (loading) {
    return (
      <Box className="td-loading">
        <div className="circular-progress"></div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="td-error">{error}</Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box className="home-container">
        {/* Animated Background Particles */}
        <div className="particles">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        <Box className="td-root">
          <Box className="td-container">
            {/* Transaction Chain Details card */}
            <Box className="td-chain-response-card card">
              <Typography variant="h6" className="td-section-title">
                Transaction Chain Details
              </Typography>
              {chainData ? (
                <div className="table-container">
                  <table className="table">
                    <thead className="table-head">
                      <tr>
                        <th className="table-cell-head">Field</th>
                        <th className="table-cell-head">Value</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      <tr className="table-row">
                        <td className="table-cell">Last Updated</td>
                        <td className="table-cell">{formatDate(chainData.lastUpdateDate)}</td>
                      </tr>
                      <tr className="table-row">
                        <td className="table-cell">Created</td>
                        <td className="table-cell">{formatDate(chainData.createdDate)}</td>
                      </tr>
                      <tr className="table-row">
                        <td className="table-cell">Required Documents</td>
                        <td className="table-cell">{getRequiredDocumentIcons()}</td>
                      </tr>
                      <tr className="table-row">
                        <td className="table-cell">Has Validations</td>
                        <td className="table-cell">
                          <div className="flex-container">
                            <span
                              className={`chip ${chainData.has_validations ? 'chip-success' : 'chip-default'}`}
                            >
                              {chainData.has_validations ? "Yes" : "No"}
                            </span>
                            {chainData.has_validations && (
                              <RerunValidationsButton
                                onClick={handleRerunValidations}
                                isRerunningValidations={isRerunningValidations}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                      {chainData.has_validations && (
                        <tr className="table-row">
                          <td className="table-cell">Validations</td>
                          <td className="table-cell">
                            <div className="flex-container flex-wrap">
                              {chainData.validations.map((validation, index) => (
                                <span key={index} className="chip chip-outlined">{validation}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <span className="text-secondary">No transaction chain data available.</span>
              )}
            </Box>

            {/* Worker Error */}
            {workerError && (
              <Box className="td-error">{workerError}</Box>
            )}

            {/* Transaction Chain Section */}
            <Box className="td-chain-section">
              <Box className="td-chain-header">
                <Typography variant="h6" className="td-chain-title">
                  Transaction Chain
                </Typography>
                {(chainData?.has_validations || validations.length > 0) && (
                  <Button
                    className="validation-stepper-button secondary btn-contained"
                    onClick={handleRuntimeValidation}
                    disabled={isFetchingValidations}
                    sx={chainData?.has_validations ? { animation: 'pulse 2s infinite' } : {}}
                  >
                    {isFetchingValidations ? <CircularProgress size={24} /> : 'View Validations'}
                  </Button>
                )}
              </Box>
              {filteredFiles.length > 0 ? (
                <div className="td-chain-chips">
                  {filteredFiles.map((file, index) => (
                    <span
                      key={index}
                      className={`chip ${selectedFileIndex === index ? 'chip-primary' : 'chip-default'}`}
                      onClick={() => handleFileSelect(index)}
                    >
                      {file.docType}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-secondary">No documents available.</span>
              )}
            </Box>

            <div className="td-divider"></div>

            {/* PDF Preview and Extracted Data */}
            <Box className="td-main-layout">
              <Box className="td-preview-section card">
                <Typography variant="h6" className="td-preview-title">
                  PDF Preview
                </Typography>
                {pdfUrl ? (
                  workerError ? (
                    <Box className="td-error">
                      PDF preview unavailable due to worker error. Please try again later.
                    </Box>
                  ) : (
                    <Worker workerUrl={workerError ? "" : workerSrc}>
                      <Viewer
                        fileUrl={pdfUrl}
                        plugins={[defaultLayoutPluginInstance]}
                        className="td-preview-container"
                      />
                    </Worker>
                  )
                ) : (
                  <span className="td-preview-placeholder">
                    No document selected or PDF unavailable.
                  </span>
                )}
              </Box>

              <Box className="td-data-section card">
                <Typography variant="h6" className="td-data-title">
                  Extracted Data
                </Typography>
                {dataError ? (
                  <Box className="td-error">{dataError}</Box>
                ) : extractedData ? (
                  <>
                    {console.log("Rendering JsonEditor with finalized_data:", extractedData)}
                    <JsonEditor
                      jsonData={extractedData}
                      onChange={() => {}}
                      isNew={false}
                      isAdmin={false}
                      transactionId={transactionId}
                      documentId={transaction?.files[selectedFileIndex]?.id || null}/>
                  </>
                ) : (
                  <span className="td-data-placeholder">No finalized data available.</span>
                )}
              </Box>
            </Box>

            <div className="td-divider"></div>

            {/* Alerts and Audit Trail */}
            <AlertsAndNotifications 
              alerts={transactionAlerts} 
              onAcknowledge={onAcknowledgeAlert} 
            />
            <AuditTrail auditTrail={auditTrail} />
          </Box>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default TransactionDetails;